import EmailIcon from '@listening/assets/icons/email.svg?react';
import PasswordIcon from '@listening/assets/icons/password.svg?react';
import logoSmall from '@listening/assets/imgs/logo/logoSmall.svg';
import { Effect, Schema, Predicate } from 'effect';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { Button } from '@listening/frontend/components/radix/button';
import { Input } from '@listening/frontend/components/radix/input';
import { cn } from '@listening/utils/react-helpers';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Circle,
  Headset,
  Hourglass,
  Lock,
  Mail,
  NavArrowLeft,
  WarningCircle,
} from 'iconoir-react';
import { CheckCircle } from 'iconoir-react/solid';
import type { ReactNode } from 'react';
import { AuthCard } from '@/components/web-fe/auth/auth-card';
import { authInputClassName } from '@/components/web-fe/auth/auth-form-inputs';
import { AuthPrimaryButton } from '@/components/web-fe/auth/auth-primary-button';
import { PasswordInput } from '@/components/web-fe/auth/password-input';
import { optionalCatching, optionalCatchingTo } from '@/lib/search-params';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';
import { seo } from '@/seo/seo';
import {
  completePasswordReset,
  requestPasswordReset,
} from '@/services/auth-service';

const MIN_PASSWORD_LENGTH = 8;

const resetSteps = ['request', 'check-email', 'verify', 'success'] as const;

const emailFormSchema = Schema.toStandardSchemaV1(
  Schema.Struct({ email: SchemaUtils.Email }),
);

const passwordFormSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    password: Schema.String.check(
      Schema.isMinLength(MIN_PASSWORD_LENGTH, {
        message: m.password_reset_password_min(),
      }),
    ),
  }),
);

export const Route = createFileRoute('/_marketing/password-reset')({
  validateSearch: Schema.toStandardSchemaV1(
    Schema.Struct({
      // Always resolves to a step, so callers can branch on it directly. The two
      // combinators cover different failures: `withDecodingDefaultType`
      // supplies the value when the key is MISSING (which fails at the struct,
      // before any field decoder runs), and `catchDecoding` recovers when the
      // key is present but junk.
      step: Schema.Literals(resetSteps).pipe(
        Schema.catchDecoding(() => Effect.succeedSome('request' as const)),
        Schema.withDecodingDefaultType(Effect.succeed('request' as const)),
      ),
      email: optionalCatchingTo(Schema.String, ''),
      token: optionalCatching(Schema.String),
      error: optionalCatching(Schema.String),
    }),
  ),
  head: () => ({
    meta: [
      ...seo({
        title: m.password_reset_meta_title(),
        description: m.password_reset_meta_description(),
      }),
    ],
  }),
  component: () => <PasswordResetPage />,
});

function PasswordResetPage() {
  const { step, token, error } = Route.useSearch();

  return (
    <AuthCard>
      <div className='flex w-full flex-col items-center'>
        <img alt={m.auth_logo_alt()} className='w-[37px]' src={logoSmall} />
        <div className='mt-8 flex w-full max-w-[420px] flex-col items-center sm:mt-12'>
          {error ? (
            <PasswordResetLinkExpired />
          ) : token && step !== 'success' ? (
            <PasswordResetWithToken token={token} />
          ) : step === 'check-email' ? (
            <PasswordResetCheckEmail />
          ) : step === 'success' ? (
            <PasswordResetSuccess />
          ) : step === 'verify' ? (
            <PasswordResetLinkExpired />
          ) : (
            <PasswordResetRequest />
          )}
        </div>
        <a
          className='mt-10 inline-flex items-center justify-center gap-2 text-sm font-medium text-[#102087] hover:underline dark:text-[#818CF8]'
          href='https://help.listening.com'
          rel='noopener noreferrer'
          target='_blank'
        >
          <Headset className='size-4' />
          {m.auth_contact_support()}
        </a>
      </div>
    </AuthCard>
  );
}

function StepHeader({
  icon,
  tone,
  title,
  description,
}: {
  icon: ReactNode;
  tone: 'brand' | 'success' | 'warning';
  title: string;
  description: string;
}) {
  return (
    <div className='flex flex-col items-center text-center'>
      <div
        className={cn(
          'flex size-14 items-center justify-center rounded-full [&>svg]:size-7',
          tone === 'brand' &&
            'bg-royal-50/10 text-royal-50 dark:bg-indigo-400/15 dark:text-indigo-300',
          tone === 'success' &&
            'bg-green-500/10 text-green-600 dark:text-green-400',
          tone === 'warning' &&
            'bg-amber-500/10 text-amber-600 dark:text-amber-400',
        )}
      >
        {icon}
      </div>
      <h1 className='mt-6 font-jakarta text-3xl leading-[1.1] font-bold tracking-tight text-neutral-900 dark:text-white'>
        {title}
      </h1>
      <p className='mt-3 font-jakarta text-base text-gray-500 dark:text-gray-400'>
        {description}
      </p>
    </div>
  );
}

function BackToLogin() {
  return (
    <Link
      className='inline-flex items-center gap-1.5 font-jakarta text-sm font-semibold text-gray-500 hover:text-neutral-900 dark:text-gray-400 dark:hover:text-white'
      to={localizeHref('/app/login')}
    >
      <NavArrowLeft className='size-4' />
      {m.mobile_back_to_login()}
    </Link>
  );
}

function FormError({ message }: { message: string }) {
  return (
    <p
      className='flex items-start gap-2 rounded-2xl bg-red-50 px-4 py-3 text-left text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400'
      role='alert'
    >
      <WarningCircle className='mt-0.5 size-4 shrink-0' />
      {message}
    </p>
  );
}

function PasswordResetRequest() {
  const { email: initialEmail } = Route.useSearch();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: { email: initialEmail ?? '' },
    validators: {
      onChange: emailFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await requestPasswordReset(value.email);
        } catch {
          return m.password_reset_failed_send();
        }
        await navigate({
          to: '/password-reset',
          search: { step: 'check-email', email: value.email },
        });
      },
    },
  });

  return (
    <>
      <StepHeader
        description={m.password_reset_request_body()}
        icon={<Mail />}
        title={m.auth_forgot_password()}
        tone='brand'
      />
      <form
        className='mt-8 flex w-full flex-col gap-4'
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field
          name='email'
          children={(field) => (
            <div className='flex flex-col gap-2'>
              <label className='sr-only' htmlFor={field.name}>
                {m.common_email_address()}
              </label>
              <Input
                autoComplete='email'
                autoFocus
                className={authInputClassName}
                icon={<EmailIcon className='text-secondary-foreground' />}
                id={field.name}
                inputMode='email'
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                }}
                placeholder={m.auth_email_placeholder()}
                type='email'
                value={field.state.value}
                wrapperClassName='w-full'
              />
              {field.state.meta.isTouched && !field.state.meta.isValid ? (
                <p className='px-4 text-left text-xs text-red-600 dark:text-red-400'>
                  {toErrorMessages(field.state.meta.errors).join(', ')}
                </p>
              ) : null}
            </div>
          )}
        />
        <form.Subscribe
          selector={(state) =>
            [state.errorMap.onSubmit, state.isSubmitting] as const
          }
          children={([submitError, isSubmitting]) => (
            <>
              {Predicate.isString(submitError) ? (
                <FormError message={submitError} />
              ) : null}
              <div className='mt-2'>
                <AuthPrimaryButton isPending={isSubmitting}>
                  {m.password_reset_send_link()}
                </AuthPrimaryButton>
              </div>
            </>
          )}
        />
      </form>
      <div className='mt-8'>
        <BackToLogin />
      </div>
    </>
  );
}

function PasswordResetCheckEmail() {
  const { email } = Route.useSearch();
  const resend = useMutation({ mutationFn: requestPasswordReset });

  return (
    <>
      <StepHeader
        description={m.password_reset_sent_body()}
        icon={<Mail />}
        title={m.password_reset_check_email()}
        tone='brand'
      />
      {email ? (
        <p className='mt-6 max-w-full truncate rounded-full bg-gray-100 px-5 py-2.5 font-jakarta text-base font-semibold text-neutral-900 dark:bg-gray-900/30 dark:text-white'>
          {email}
        </p>
      ) : null}
      <p className='mt-4 text-center font-jakarta text-sm text-gray-500 dark:text-gray-400'>
        {m.password_reset_link_expiry()}
      </p>
      <div className='mt-8 flex w-full flex-col items-center gap-4'>
        {email ? (
          <Button
            className='h-[52px] w-full rounded-full border border-gray-200 bg-white font-jakarta text-base font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-900/10 dark:bg-gray-900/10 dark:text-steel-260 dark:hover:bg-gray-900/20'
            disabled={resend.isPending}
            onClick={() => {
              resend.mutate(email);
            }}
            type='button'
            variant='outline'
          >
            {m.password_reset_resend()}
          </Button>
        ) : null}
        {resend.isSuccess ? (
          <p
            className='text-sm font-medium text-green-600 dark:text-green-400'
            role='status'
          >
            {m.password_reset_resent()}
          </p>
        ) : null}
        {resend.isError ? (
          <FormError message={m.password_reset_failed_send()} />
        ) : null}
        <Link
          className='font-jakarta text-sm font-semibold text-royal-50 hover:underline dark:text-indigo-400'
          search={{ step: 'request' }}
          to={localizeHref('/password-reset')}
        >
          {m.password_reset_use_different_email()}
        </Link>
      </div>
      <div className='mt-8'>
        <BackToLogin />
      </div>
    </>
  );
}

function PasswordResetLinkExpired() {
  const { email } = Route.useSearch();

  return (
    <>
      <StepHeader
        description={m.password_reset_link_expired_body()}
        icon={<Hourglass />}
        title={m.password_reset_link_expired_title()}
        tone='warning'
      />
      <div className='mt-8 w-full'>
        <AuthPrimaryButton asChild>
          <Link
            search={{ step: 'request', email }}
            to={localizeHref('/password-reset')}
          >
            {m.password_reset_request_new_link()}
          </Link>
        </AuthPrimaryButton>
      </div>
      <div className='mt-8'>
        <BackToLogin />
      </div>
    </>
  );
}

function PasswordResetWithToken({ token }: { token: string }) {
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: { password: '' },
    validators: {
      onChange: passwordFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await completePasswordReset({ token, newPassword: value.password });
        } catch {
          return m.password_reset_failed_expired();
        }
        await navigate({
          to: '/password-reset',
          search: { step: 'success' },
        });
      },
    },
  });

  return (
    <>
      <StepHeader
        description={m.password_reset_new_password_body()}
        icon={<Lock />}
        title={m.password_reset_set_new()}
        tone='brand'
      />
      <form
        className='mt-8 flex w-full flex-col gap-4'
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field
          name='password'
          children={(field) => (
            <div className='flex flex-col gap-3'>
              <label className='sr-only' htmlFor={field.name}>
                {m.password_reset_new_password_label()}
              </label>
              <PasswordInput
                appearance='auth'
                autoComplete='new-password'
                autoFocus
                className={authInputClassName}
                icon={<PasswordIcon className='text-secondary-foreground' />}
                iconPosition='left'
                id={field.name}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                }}
                placeholder={m.password_reset_new_password_label()}
                value={field.state.value}
                wrapperClassName='w-full'
              />
              <PasswordRule
                met={field.state.value.length >= MIN_PASSWORD_LENGTH}
              />
            </div>
          )}
        />
        <form.Subscribe
          selector={(state) =>
            [
              state.errorMap.onSubmit,
              state.isSubmitting,
              state.canSubmit,
            ] as const
          }
          children={([submitError, isSubmitting, canSubmit]) => (
            <>
              {Predicate.isString(submitError) ? (
                <div className='flex flex-col items-center gap-3'>
                  <FormError message={submitError} />
                  <Link
                    className='font-jakarta text-sm font-semibold text-royal-50 hover:underline dark:text-indigo-400'
                    search={{ step: 'request' }}
                    to={localizeHref('/password-reset')}
                  >
                    {m.password_reset_request_new_link()}
                  </Link>
                </div>
              ) : null}
              <div className='mt-2'>
                <AuthPrimaryButton
                  disabled={!canSubmit}
                  isPending={isSubmitting}
                >
                  {m.password_reset_save_password()}
                </AuthPrimaryButton>
              </div>
            </>
          )}
        />
      </form>
      <div className='mt-8'>
        <BackToLogin />
      </div>
    </>
  );
}

function PasswordRule({ met }: { met: boolean }) {
  return (
    <p
      className={cn(
        'flex items-center gap-2 px-4 font-jakarta text-sm font-medium transition-colors',
        met
          ? 'text-green-600 dark:text-green-400'
          : 'text-gray-500 dark:text-gray-400',
      )}
    >
      {met ? <CheckCircle className='size-4' /> : <Circle className='size-4' />}
      {m.password_reset_rule_min_length()}
    </p>
  );
}

function PasswordResetSuccess() {
  return (
    <>
      <StepHeader
        description={m.password_reset_updated_body()}
        icon={<CheckCircle />}
        title={m.password_reset_updated_title()}
        tone='success'
      />
      <div className='mt-8 w-full'>
        <AuthPrimaryButton asChild>
          <Link to={localizeHref('/app/login')}>{m.common_login()}</Link>
        </AuthPrimaryButton>
      </div>
    </>
  );
}

const isStandardIssue = (e: unknown): e is { message: string } =>
  Predicate.isObjectOrArray(e) && 'message' in e;

const toErrorMessages = (errors: Array<unknown>) =>
  errors.flatMap((e) =>
    Predicate.isString(e) ? [e] : isStandardIssue(e) ? [e.message] : [],
  );
